import React, { useState } from 'react';
import { Card, Button, Typography, Steps, message, Alert, Spin, Table, Tag, Input } from 'antd';
import { PlayCircleOutlined, SendOutlined, ReloadOutlined, CheckCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { api } from '../services/api';

const { Title, Text } = Typography;
const { Step } = Steps;
const { TextArea } = Input;

const FileProcessing = ({ uploadedFile, selectedTemplate, columnMappings, onProcessingComplete, onReset }) => {
    // Check if we're in passthrough mode
    const isPassthroughMode = selectedTemplate?.isPassthrough === true;
    const [loading, setLoading] = useState(false);
    const [processedFile, setProcessedFile] = useState(null);
    const [finalFilename, setFinalFilename] = useState('');
    const [generatedPreview, setGeneratedPreview] = useState(null);
    const [fileType, setFileType] = useState('');
    const [step, setStep] = useState(0); // 0=Generate, 1=Send, 2=Complete
    const [airflowStatus, setAirflowStatus] = useState(null); // Airflow DAG status
    const [airflowPolling, setAirflowPolling] = useState(false);
    const [airflowDagRunId, setAirflowDagRunId] = useState(null);
    const [, setAirflowDagId] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [fileDescription, setFileDescription] = useState('');

    const steps = [
        {
            title: 'Generate File',
            description: 'Process file according to template and mappings',
        },
        {
            title: 'Send to Invent',
            description: 'Send file to Invent (catalog or storage)',
        },
        {
            title: 'Load to Delta',
            description: 'Airflow DAG loading data to Delta Lake',
        },
        {
            title: 'Complete',
            description: 'File successfully processed and loaded',
        },
    ];

    const handleGenerateFile = async () => {
        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);

            let response;

            if (isPassthroughMode) {
                // Passthrough mode - use different endpoint
                const { customFilename, columnRenames } = columnMappings;
                formData.append('custom_filename', customFilename || 'custom_upload');
                formData.append('column_renames', JSON.stringify(columnRenames || {}));

                response = await api.post('/process-passthrough', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                // Regular template mode
                const templateKey = selectedTemplate.templateKey || selectedTemplate.name.toLowerCase().replace(/\s+/g, '_');
                formData.append('template_name', templateKey);
                formData.append('mappings', JSON.stringify(columnMappings));

                response = await api.post('/process', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }

            if (response.data.success) {
                setProcessedFile(response.data.file_id);
                setFinalFilename(response.data.final_filename);
                setGeneratedPreview(response.data.preview_data);
                setFileType(response.data.file_type);
                setStep(1); // Move to Send step

                message.success(isPassthroughMode ? 'File processed in passthrough mode!' : 'File generated successfully!');
                onProcessingComplete(response.data.file_id);
            } else {
                message.error('Failed to generate file');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
            message.error(`File generation failed: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    const pollAirflowStatus = async (dagId, dagRunId) => {
        try {
            const response = await api.get(`/airflow-status/${dagRunId}`);
            
            if (response.data.success) {
                const state = response.data.state;
                setAirflowStatus(state);
                
                // Terminal states: success, failed
                if (state === 'success') {
                    setStep(3); // Move to Complete step
                    setAirflowPolling(false);
                    message.success('Data successfully loaded to Delta Lake!');
                    return true;
                } else if (state === 'failed') {
                    setAirflowPolling(false);
                    message.error('Airflow DAG failed. Please check Airflow logs.');
                    return true;
                }
                
                // Continue polling for: queued, running
                return false;
            }
        } catch (error) {
            console.error('Error polling Airflow status:', error);
            // Don't stop polling on error, might be temporary
            return false;
        }
    };

    const startAirflowPolling = (dagId, dagRunId) => {
        setAirflowPolling(true);
        setAirflowDagId(dagId);
        setAirflowDagRunId(dagRunId);
        
        // Poll every 5 seconds
        const pollInterval = setInterval(async () => {
            const isDone = await pollAirflowStatus(dagId, dagRunId);
            if (isDone) {
                clearInterval(pollInterval);
            }
        }, 5000);
        
        // Initial poll
        pollAirflowStatus(dagId, dagRunId);
        
        // Cleanup on unmount
        return () => clearInterval(pollInterval);
    };

    const handleSendToInvent = async () => {
        setLoading(true);

        try {
            const response = await api.post('/send-to-azure', {
                file_id: processedFile,
                filename: finalFilename,
                template_name: isPassthroughMode 
                    ? (columnMappings?.customFilename || 'passthrough') 
                    : (selectedTemplate?.filename_template || 'unknown'),
                description: fileDescription?.trim() || undefined,
            });

            if (response.data.success) {
                const dest = response.data.destination;
                setUploadResult({
                    success: true,
                    destination: dest,
                    filename: finalFilename,
                    timestamp: new Date().toLocaleString(),
                    container_path: response.data.container_path,
                    catalog: response.data.catalog,
                    schema: response.data.schema,
                    table: response.data.table,
                    rows_inserted: response.data.rows_inserted,
                    airflow_triggered: response.data.airflow_triggered,
                    staging_used: response.data.staging_used,
                    message: response.data.message,
                });

                if (dest === 'trino_iceberg') {
                    const msg = response.data.message || 'File saved to catalog successfully!';
                    message.success(response.data.staging_used ? 'Step 1: Azure → Step 2: Iceberg. ' + msg : msg);
                    setStep(3);
                } else {
                    message.success(response.data.message || 'File sent to Invent successfully!');
                    if (response.data.airflow_triggered && response.data.airflow_dag_run_id) {
                        setStep(2);
                        setAirflowStatus(response.data.airflow_state || 'queued');
                        startAirflowPolling(response.data.airflow_dag_id, response.data.airflow_dag_run_id);
                    } else {
                        setStep(3);
                        if (response.data.airflow_error) {
                            message.warning(`Note: ${response.data.airflow_error}`);
                        }
                    }
                }
            } else {
                message.error('Failed to send file to Invent');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message;
            message.error(`Send failed: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setProcessedFile(null);
        setFinalFilename('');
        setGeneratedPreview(null);
        setFileType('');
        setStep(0);
        setAirflowStatus(null);
        setAirflowPolling(false);
        setAirflowDagRunId(null);
        setAirflowDagId(null);
        setUploadResult(null);
        setFileDescription('');
    };

    const handleStartOver = () => {
        onReset();
    };

    const getStepStatus = (stepIndex) => {
        if (stepIndex < step) return 'finish';
        if (stepIndex === step) return 'process';
        return 'wait';
    };

    return (
        <div>
            <Title level={4}>Process and Send File</Title>
            <Text type="secondary">
                Generate the final file according to your mappings and send it to Invent (catalog or storage).
            </Text>

            <div style={{ marginTop: 24 }}>
                <Steps current={step} status={getStepStatus(step)}>
                    {steps.map((stepItem, index) => (
                        <Step
                            key={index}
                            title={stepItem.title}
                            description={stepItem.description}
                            icon={
                                index === 2 && step === 2 ? (
                                    <CheckCircleOutlined />
                                ) : undefined
                            }
                        />
                    ))}
                </Steps>
            </div>

            <div style={{ marginTop: 32 }}>
                {step === 0 && (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <Title level={4}>
                                {isPassthroughMode ? (
                                    <>
                                        <ThunderboltOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                        Ready to Process (Passthrough Mode)
                                    </>
                                ) : (
                                    'Ready to Generate File'
                                )}
                            </Title>

                            {isPassthroughMode ? (
                                <div style={{ marginBottom: 24 }}>
                                    <Tag color="gold" style={{ marginBottom: 12 }}>Passthrough Mode</Tag>
                                    <br />
                                    <Text>
                                        <strong>Mode:</strong> Direct Upload (No Template Validation)
                                    </Text>
                                    <br />
                                    <Text>
                                        <strong>Output Filename:</strong> {columnMappings?.customFilename || 'custom_upload'}_[timestamp].csv
                                    </Text>
                                    <br />
                                    <Text>
                                        <strong>Renamed Columns:</strong>{' '}
                                        {columnMappings?.columnRenames && Object.keys(columnMappings.columnRenames).length > 0
                                            ? `${Object.keys(columnMappings.columnRenames).length} column(s)`
                                            : 'None (keeping original names)'
                                        }
                                    </Text>
                                </div>
                            ) : (
                                <div style={{ marginBottom: 24 }}>
                                    <Text>
                                        <strong>Template:</strong> {selectedTemplate?.name}
                                    </Text>
                                    <br />
                                    <Text>
                                        <strong>Mapped Fields:</strong> {Array.isArray(columnMappings) ? columnMappings.length : 0}
                                    </Text>
                                    <br />
                                    <Text>
                                        <strong>Output Filename:</strong> {selectedTemplate?.filename_template}_YYYY-MM-DDTHH-MM-SS.csv
                                    </Text>
                                </div>
                            )}

                            <Button
                                type="primary"
                                size="large"
                                icon={isPassthroughMode ? <ThunderboltOutlined /> : <PlayCircleOutlined />}
                                onClick={handleGenerateFile}
                                loading={loading}
                                style={isPassthroughMode ? { background: '#faad14', borderColor: '#faad14' } : {}}
                            >
                                {isPassthroughMode ? 'Process File' : 'Generate File'}
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 1 && (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <Title level={4}>File Generated Successfully</Title>
                            <div style={{ marginBottom: 24 }}>
                                <Text>
                                    <strong>Final Filename:</strong> {finalFilename}
                                </Text>
                                <br />
                                <Text>
                                    <strong>File Type:</strong> {fileType}
                                </Text>
                            </div>


                            {/* PREVIEW TABLE */}
                            {generatedPreview && Array.isArray(generatedPreview) && generatedPreview.length > 0 && generatedPreview[0] && (
                                <Card title="Generated File Preview" style={{ marginBottom: 24, textAlign: 'left' }}>
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong>Columns: </Text>
                                        <Text>{Object.keys(generatedPreview[0] || {}).length}</Text>
                                        <br />
                                        <Text strong>Preview rows: </Text>
                                        <Text>{generatedPreview.length}</Text>
                                    </div>

                                    <Table
                                        dataSource={generatedPreview.map((row, index) => ({ ...row, key: index }))}
                                        columns={Object.keys(generatedPreview[0] || {}).map(key => ({
                                            title: key,
                                            dataIndex: key,
                                            key: key,
                                            ellipsis: true,
                                            render: (text) => text !== null && text !== undefined ? String(text) : '',
                                        }))}
                                        pagination={false}
                                        size="small"
                                        scroll={{ x: true }}
                                    />
                                </Card>
                            )}

                            {(!generatedPreview || !Array.isArray(generatedPreview) || generatedPreview.length === 0 || !generatedPreview[0]) && (
                                <Alert
                                    message="No Preview Available"
                                    description="The generated file has no preview data, but the file was created successfully."
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 24 }}
                                />
                            )}

                            <div style={{ marginBottom: 24, textAlign: 'left' }}>
                                <Text strong>Description (optional)</Text>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                                    Add a description of what this file is about. It will be stored in the table as file_description.
                                </Text>
                                <TextArea
                                    placeholder="e.g. Monthly reconciliation for January 2025"
                                    value={fileDescription}
                                    onChange={(e) => setFileDescription(e.target.value)}
                                    rows={2}
                                    maxLength={2000}
                                    showCount
                                />
                            </div>

                            <Button
                                type="primary"
                                size="large"
                                icon={<SendOutlined />}
                                onClick={handleSendToInvent}
                                loading={loading}
                            >
                                Send to Invent
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 2 && (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            {/* Send success banner (Azure or catalog) */}
                            {uploadResult && (
                                <Alert
                                    message={
                                        <span>
                                            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                            {uploadResult.destination === 'trino_iceberg'
                                                ? 'File saved to catalog'
                                                : 'File successfully sent to Invent'}
                                        </span>
                                    }
                                    description={
                                        <div style={{ textAlign: 'left' }}>
                                            <div><strong>Filename:</strong> {uploadResult.filename}</div>
                                            {uploadResult.destination === 'trino_iceberg' ? (
                                                <>
                                                    {uploadResult.staging_used && <div><strong>Flow:</strong> Azure staging → Iceberg catalog</div>}
                                                    <div><strong>Catalog:</strong> {uploadResult.catalog}.{uploadResult.schema}.{uploadResult.table}</div>
                                                    {uploadResult.rows_inserted != null && <div><strong>Rows:</strong> {uploadResult.rows_inserted}</div>}
                                                </>
                                            ) : (
                                                <div><strong>Container:</strong> {uploadResult.container_path}</div>
                                            )}
                                            <div><strong>Sent at:</strong> {uploadResult.timestamp}</div>
                                        </div>
                                    }
                                    type="success"
                                    showIcon={false}
                                    style={{ marginBottom: 24, textAlign: 'left' }}
                                />
                            )}

                            <Title level={4}>Loading Data to Delta Lake</Title>
                            <Alert
                                message="Please do not leave this page"
                                description="Your file is being loaded to Delta Lake. You can monitor the progress below."
                                type="info"
                                showIcon
                                style={{ marginBottom: 24, textAlign: 'left' }}
                            />
                            
                            <div style={{ marginBottom: 24 }}>
                                <Spin size="large" />
                                <div style={{ marginTop: 16 }}>
                                    <Text strong>Airflow DAG Status: </Text>
                                    <Text 
                                        style={{ 
                                            color: airflowStatus === 'success' ? '#52c41a' : 
                                                   airflowStatus === 'failed' ? '#ff4d4f' : 
                                                   airflowStatus === 'running' ? '#1890ff' : '#faad14'
                                        }}
                                    >
                                        {airflowStatus ? airflowStatus.toUpperCase() : 'QUEUED'}
                                    </Text>
                                </div>
                                {airflowDagRunId && (
                                    <div style={{ marginTop: 8 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            DAG Run ID: {airflowDagRunId}
                                        </Text>
                                    </div>
                                )}
                            </div>

                            <div style={{ marginTop: 24 }}>
                                {airflowStatus === 'queued' && (
                                    <Alert
                                        message="Queued"
                                        description="Your job is in the queue and will start shortly."
                                        type="info"
                                        showIcon
                                    />
                                )}
                                {airflowStatus === 'running' && (
                                    <Alert
                                        message="Running"
                                        description="Your data is being processed and loaded to Delta Lake."
                                        type="info"
                                        showIcon
                                    />
                                )}
                                {airflowStatus === 'failed' && (
                                    <Alert
                                        message="Failed"
                                        description="The data loading process failed. Please check Airflow logs or contact support."
                                        type="error"
                                        showIcon
                                    />
                                )}
                            </div>
                        </div>
                    </Card>
                )}

                {step === 3 && (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
                            <Title level={4} style={{ color: '#52c41a' }}>
                                Process Completed Successfully!
                            </Title>
                            <div style={{ marginBottom: 24 }}>
                                <Text>
                                    Your file <Text code>{finalFilename}</Text> has been successfully processed
                                    {uploadResult?.destination === 'trino_iceberg'
                                        ? ' and saved to the catalog.'
                                        : ', sent to storage, and loaded to Delta Lake.'}
                                </Text>
                            </div>
                            {uploadResult?.destination === 'trino_iceberg' && uploadResult?.catalog && (
                                <div style={{ marginBottom: 24 }}>
                                    {uploadResult.staging_used && (
                                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                                            Flow: Azure staging → Iceberg catalog
                                        </Text>
                                    )}
                                    <Text type="secondary">
                                        Table: <Text code>{uploadResult.catalog}.{uploadResult.schema}.{uploadResult.table}</Text>
                                        {uploadResult.rows_inserted != null && ` (${uploadResult.rows_inserted} rows)`}
                                    </Text>
                                </div>
                            )}

                            {airflowDagRunId && (
                                <div style={{ marginBottom: 24 }}>
                                    <Text type="secondary">
                                        Airflow DAG Run ID: <Text code>{airflowDagRunId}</Text>
                                    </Text>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
                                <Button
                                    size="large"
                                    icon={<ReloadOutlined />}
                                    onClick={handleReset}
                                >
                                    Process Another File
                                </Button>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<ReloadOutlined />}
                                    onClick={handleStartOver}
                                >
                                    Start Over
                                </Button>
                            </div>
                        </div>
                    </Card>
                )}

                {loading && step < 2 && (
                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            <Text>
                                {step === 0 && 'Generating file...'}
                                {step === 1 && 'Sending to Invent...'}
                            </Text>
                        </div>
                    </div>
                )}
            </div>

            {step < 2 && (
                <Alert
                    message="Important"
                    description="The processed file will be automatically deleted from the server after successful send to maintain security and storage efficiency."
                    type="info"
                    showIcon
                    style={{ marginTop: 24 }}
                />
            )}
            
            {step === 2 && airflowPolling && (
                <Alert
                    message="Do Not Leave This Page"
                    description="Your file is being loaded to Delta Lake. Please wait for the process to complete. This usually takes a few minutes."
                    type="warning"
                    showIcon
                    style={{ marginTop: 24 }}
                />
            )}
        </div>
    );
};

export default FileProcessing;
